import type { AuthResponse, MeResponse } from '../../entities/user/types';
import { apiFetch, authHeader, jsonBody } from '../../shared/api/httpClient';
import { e164Digits } from '../../shared/country/countryConfig';
import { activeCountry } from '../../shared/country/countryStore';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { clientHeaders } from '../../shared/lib/device';

export type LoginPayload = {
  identifier: string;
  password: string;
};

export type RegisterPayload = {
  phone?: string;
  email?: string;
  confirmPassword: string;
  turnstileToken: string;
  password: string;
  inviteCode?: string;
  acceptedTerms?: boolean;
  acceptedMarketing?: boolean;
};

export type ResetPasswordPayload = {
  identifier: string;
  password: string;
  otp: string;
};

export type OtpLoginPayload = {
  identifier: string;
  otp: string;
};

function identifierPayload(identifier: string) {
  const value = identifier.trim();
  const digits = value.replace(/\D/g, '');
  if (value.includes('@')) return { email: value.toLowerCase() };
  if (/^[A-Za-z][A-Za-z0-9._]{2,31}$/.test(value)) return { username: value };
  if (digits.length >= 10) return { phone: e164Digits(value, activeCountry()) };
  return { username: value };
}

function otpIdentifierPayload(identifier: string) {
  const value = identifier.trim();
  if (value.includes('@')) return { email: value.toLowerCase() };
  return { phone: e164Digits(value, activeCountry()) };
}

export function login(payload: LoginPayload) {
  return apiFetch<AuthResponse>('/api/auth/login', {
    method: 'POST',
    headers: clientHeaders(),
    body: jsonBody({
      ...identifierPayload(payload.identifier),
      password: payload.password,
    }),
  });
}

/**
 * Registration uses an explicit phone or email identifier plus a verified Turnstile token.
 *
 * No identifier sniffing here: the form states which it is, and guessing a second
 * time only creates hard-to-trace disagreements (a numeric e-mail local part used
 * to read as a phone number). Email signups select an active market; phone
 * signups continue deriving their country from the supplied phone prefix.
 */
export function register(payload: RegisterPayload) {
  const locale = useI18nStore.getState().language;
  return apiFetch<AuthResponse>('/api/auth/register', {
    method: 'POST',
    headers: clientHeaders(),
    body: jsonBody({
      ...(payload.email !== undefined
        ? { email: payload.email.trim().toLowerCase(), countryCode: activeCountry().code }
        : { phone: e164Digits(payload.phone || '', activeCountry()) }),
      confirmPassword: payload.confirmPassword,
      turnstileToken: payload.turnstileToken,
      password: payload.password,
      locale,
      invite_code: payload.inviteCode || '',
      accepted_terms: !!payload.acceptedTerms,
      accepted_marketing: !!payload.acceptedMarketing,
    }),
  });
}

/**
 * Existence precheck before any OTP send (anti-abuse: the backend must not
 * fan out SMS/e-mail for identifiers that have no account). Server contract:
 * POST /api/auth/otp-precheck { email|phone, purpose } -> { exists: boolean }.
 * The endpoint is expected to be heavily rate-limited server-side; while the
 * backend has not shipped it yet (404/405), we fail open so login keeps
 * working against the current API.
 *
 * `'register'` asks the same existence question as `'login'`/`'reset'` — the
 * caller is the one that reads it backwards (registration wants `exists` to
 * be `false`). See `useOtpChallenge`'s register branch: it only treats this
 * as a block when `supported` is also true, so the same 404 fail-open that
 * lets login through does not silently block every registration until the
 * backend ships this purpose.
 */
export async function precheckOtpIdentifier(identifier: string, purpose: 'login' | 'reset' | 'register'): Promise<{ exists: boolean; supported: boolean }> {
  try {
    const res = await apiFetch<{ exists?: boolean; ok?: boolean }>('/api/auth/otp-precheck', {
      method: 'POST',
      headers: clientHeaders(),
      body: jsonBody({ ...otpIdentifierPayload(identifier), purpose }),
    });
    if (typeof res?.exists === 'boolean') return { exists: res.exists, supported: true };
    return { exists: true, supported: false };
  } catch (error) {
    const status = Number((error as { status?: number })?.status || 0);
    if (status === 404 || status === 405 || status === 501) return { exists: true, supported: false };
    if (status === 429) throw error; // rate limited: surface, do not send
    return { exists: true, supported: false };
  }
}

export function sendLoginOtp(identifier: string) {
  return apiFetch<{ ok?: boolean; expires_at?: number; expiresAt?: number; expires_in?: number; expiresIn?: number; retry_after_ms?: number; reason?: string }>('/api/auth/send-otp', {
    method: 'POST',
    headers: clientHeaders(),
    body: jsonBody({
      ...otpIdentifierPayload(identifier),
      purpose: 'login',
    }),
  });
}

/**
 * Registration's OTP send. Same endpoint and shape as `sendLoginOtp`, purpose
 * `'register'` — the backend answers `409 PHONE_TAKEN` (no SMS sent) when the
 * number already has an account, which is the authoritative check behind the
 * `precheckOtpIdentifier` skip above.
 */
export function sendRegisterOtp(phone: string) {
  return apiFetch<{ ok?: boolean; expires_at?: number; expiresAt?: number; expires_in?: number; expiresIn?: number; retry_after_ms?: number; reason?: string }>('/api/auth/send-otp', {
    method: 'POST',
    headers: clientHeaders(),
    body: jsonBody({
      ...otpIdentifierPayload(phone),
      purpose: 'register',
    }),
  });
}

export function loginOtp(payload: OtpLoginPayload) {
  return apiFetch<AuthResponse>('/api/auth/login-otp', {
    method: 'POST',
    headers: clientHeaders(),
    body: jsonBody({
      ...otpIdentifierPayload(payload.identifier),
      otp: payload.otp,
    }),
  });
}

export function sendForgotPasswordOtp(identifier: string) {
  return apiFetch<{ ok?: boolean; expires_at?: number; expiresAt?: number; expires_in?: number; expiresIn?: number }>('/api/auth/forgot-password/send-otp', {
    method: 'POST',
    headers: clientHeaders(),
    body: jsonBody(identifierPayload(identifier)),
  });
}

export function resetForgotPassword(payload: ResetPasswordPayload) {
  return apiFetch<{ ok?: boolean }>('/api/auth/forgot-password/reset', {
    method: 'POST',
    headers: clientHeaders(),
    body: jsonBody({
      ...identifierPayload(payload.identifier),
      password: payload.password,
      otp: payload.otp,
    }),
  });
}

export function fetchMe(token: string) {
  return apiFetch<MeResponse>('/api/me', {
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

// Exchange the current (possibly expired) access token + session secret for a
// fresh access token. The backend verifies the session by jti + secret, so this
// succeeds even after the 30-min access token has expired, as long as the
// 30-day sliding session window is still open. Returns a rotated sessionSecret.
export function refreshSessionApi(token: string, sessionSecret: string) {
  return apiFetch<AuthResponse>('/api/auth/refresh', {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody({ sessionSecret }),
  });
}

export function updateMePreferences(token: string, payload: { public_leaderboard?: boolean; activity_notifications?: boolean }) {
  return apiFetch<MeResponse>('/api/me/preferences', {
    method: 'PATCH',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody(payload),
  });
}

export type UsernameResponse = Partial<MeResponse> & {
  username?: string;
  used_grace?: boolean;
  usedGrace?: boolean;
  next_change_at?: number;
  nextChangeAt?: number;
};

export function updateMeUsername(token: string, payload: { username: string; setup?: boolean }) {
  return apiFetch<UsernameResponse>('/api/me/username', {
    method: 'PUT',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody(payload),
  });
}

export function checkUsername(token: string | null | undefined, username: string) {
  return apiFetch<{ available: boolean; reason?: string }>('/api/usernames/check', {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody({ username }),
  });
}

export function updateMeDefaultAvatar(token: string, defaultId: number) {
  return apiFetch<MeResponse>('/api/me/avatar/default', {
    method: 'PUT',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody({ default_id: defaultId }),
  });
}

export function updateMeAvatarData(token: string, avatarDataUrl: string) {
  return apiFetch<MeResponse>('/api/me/avatar', {
    method: 'PATCH',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody({ avatar_data_url: avatarDataUrl }),
  });
}
